/** The treasury strip along the bottom of the adventure screen.
 *
 * Seven resources, the calendar, and the end-turn button. It is the one panel
 * that is always on screen, so it is also the one that most has to survive a
 * 1366x768 window: the resource plates shrink before the numbers do, and the
 * date drops to its short form rather than wrapping.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import { RESOURCES, reasonText } from "@game/core";
import type { PlayerObservation, Reason, ResourceKind } from "@game/core";
import { BitmapText } from "../theme/bitmapText.js";
import { Divider, Icon, Panel } from "../theme/components.js";
import { RESOURCE_ICON } from "../theme/art.js";
import { METRICS } from "../theme/tokens.js";
import { useObservation } from "../hooks/useGameState.js";
import { victoryConditionText } from "../screens/GameOver.js";
import { formatDate, formatDateShort, formatNumber } from "./format.js";

const RESOURCE_LABEL: Readonly<Record<ResourceKind, string>> = Object.freeze({
  gold: "Gold", wood: "Wood", ore: "Ore", mercury: "Mercury",
  sulfur: "Sulfur", crystal: "Crystal", gems: "Gems",
});

/**
 * The scenario's aim, for the strip that is always on screen.
 *
 * The condition is picked on the New Game screen and then lived with for
 * thirty turns, and until this line the only places it was ever said again
 * were the Journal - one keystroke away, but a keystroke a player has to know
 * about - and the screen that tells you you have already won or lost. A player
 * who alt-tabbed away on day 4 and came back on day 11 had nowhere on the map
 * itself to read what he was playing toward.
 *
 * `subject` is resolved by the host, not here: the argument in `capture_town:
 * town:3` is an entity id, and turning an id into a name means asking the
 * state - which is also where the fog question lives. A bar that named a town
 * the player has never seen would be a scouting report.
 */
function aimText(obs: PlayerObservation | null, subject: string | null): string | null {
  const condition = obs?.victory.condition ?? "";
  if (condition === "") return null;
  return victoryConditionText(condition, subject);
}

export function ResourceBar(props: {
  readonly onEndTurn: () => void;
  /**
   * The victory condition's argument in words - a town's name, an artifact's -
   * or null when the condition takes none. See `aimText`.
   */
  readonly conditionSubject?: string | null | undefined;
  /** Non-null disables the button and explains itself in the tooltip. */
  readonly endTurnReason?: Reason | null | undefined;
  /**
   * The host's system key - save, load, settings, quit - at the head of the
   * bar.
   *
   * It is a slot rather than a button this panel owns because the panel knows
   * nothing about the shell's dialogs. What it buys is that the key is IN the
   * bar: it used to be absolutely positioned over the map at 10,10 in the
   * full-screen-menu kit's material, which put a fourth material and a third
   * type register on the terrain the player is trying to click.
   */
  readonly leading?: ReactNode | undefined;
  readonly className?: string | undefined;
}): JSX.Element {
  const obs = useObservation();
  const resources = obs.resources;
  const blocked = props.endTurnReason ?? null;
  const aim = aimText(obs, props.conditionSubject ?? null);

  const cells = useMemo(
    () => RESOURCES.map((kind) => ({
      kind,
      label: RESOURCE_LABEL[kind],
      icon: RESOURCE_ICON[kind],
      amount: resources === null ? 0 : resources[kind],
    })),
    [resources],
  );

  return (
    <Panel className={props.className} style={{ display: "flex", alignItems: "stretch", padding: "0 var(--hm-pad)" }}>
      {props.leading !== undefined && props.leading !== null && (
        <>
          {props.leading}
          <Divider vertical />
        </>
      )}
      {/* Grouped at the left and sized to their contents.
          Each plaque used to be a seventh of the bar - about 175px carrying a
          26px icon and up to six glyphs, left-packed, so every one of the seven
          ended in 110px of blank plate and the strip held roughly 770px of dead
          surface, more than the whole sidebar. A plaque is a stamped readout;
          it should be the size of what is stamped on it. */}
      <div className="hm-adv-resrow">
        {cells.map((cell) => (
          <div
            key={cell.kind}
            className="hm-adv-res"
            title={`${cell.label}: ${formatNumber(cell.amount)}`}
          >
            {/* 96px source at 24px: an exact quarter, so the box filter is clean.
                The medallion sits in the kit's recess and screens onto its
                floor: the icon's own ground is opaque black, so unblended it
                punches a hole through the plate it is set into. */}
            <span className="hm-adv-res__socket">
              <Icon atlasKey={cell.icon} size={METRICS.iconMedium} title={cell.label}
                className="hm-relief__inlay" />
            </span>
            {/* A treasury figure is stamped into the plate, so it is a bitmap
                glyph with a keyline rather than an anti-aliased vector one -
                the same face the battlefield's count plaques use. The span
                keeps the plate's minimum width; the mark inside it is drawn at
                its own whole magnification and is never resized by CSS. */}
            <span className="hm-num hm-adv-res__value">
              <BitmapText face="romanDisplay" scale={1}>{formatNumber(cell.amount)}</BitmapText>
            </span>
          </div>
        ))}
      </div>

      {/* The aim takes what is left over and nothing more: `flex: 1 1 0` with
          `min-width: 0` means it absorbs slack on a wide screen and shrinks to
          nothing on a narrow one, so it can never push the calendar or the
          end-turn key off the end of the bar. The full sentence is always in
          the tooltip, whatever the plate has room to show. */}
      {aim === null ? null : (
        <>
          <Divider vertical />
          <div className="hm-adv-aim" title={`Victory: ${aim}`}>
            <span className="hm-adv-aim__label">
              <BitmapText face="roman" scale={1}>Aim</BitmapText>
            </span>
            <span className="hm-adv-aim__text">{aim}</span>
          </div>
        </>
      )}

      <Divider vertical />

      <div className="hm-row" style={{ gap: "calc(var(--hm-u) * 8)", flex: "0 0 auto" }}>
        {/* The calendar is stamped on a plate, not set on bare wood. It was the
            only object in the bar with no edge on it, between seven chamfered
            treasury plaques and a cast-bronze key - and both its lines were
            serif, the date in the kit's display face and "Your turn" in its
            label face, four inches from seven bitmap treasury figures. */}
        <div className="hm-adv-date">
          <span className="hm-adv-date__line">
            {/* Long form when the bar has room; the short form never clips. */}
            <span className="hm-date-long">
              <BitmapText face="roman" scale={1}>{formatDate(obs.turn)}</BitmapText>
            </span>
            <span className="hm-date-short">
              <BitmapText face="roman" scale={1}>{formatDateShort(obs.turn)}</BitmapText>
            </span>
          </span>
          <span className="hm-adv-date__line hm-adv-date__line--dim">
            <BitmapText face="roman" scale={1}>{obs.isActive ? "Your turn" : "Waiting"}</BitmapText>
          </span>
        </div>

        <button
          type="button"
          className="hm-adv-endturn"
          onClick={props.onEndTurn}
          disabled={blocked !== null}
          title={blocked === null ? "End turn (E)" : reasonText(blocked)}
        >
          <EndTurnGlyph />
          {/* Stamped, in the face this screen's headings are cut in. The label
              was the kit's display serif - a third register on a bar whose
              other twelve readings are all bitmap marks. */}
          <BitmapText face="roman" scale={1}>End Turn</BitmapText>
        </button>
      </div>
    </Panel>
  );
}

/**
 * The engraved glyph on the end-turn key: an hourglass, cut into the bronze.
 *
 * Drawn rather than pulled from the UI atlas because the atlas has no
 * end-of-turn icon, and a button with a word and no mark on it is the "grey
 * rectangle with a label" the brief forbids.
 *
 * It is a filled SHAPE, not a hairline. A 1px stroked outline is invisible at
 * 1:1 - a reviewer measured it and called it a 12px character rather than an
 * engraving - so the glass is a solid dark body with a light lower lip offset
 * under it, which is the two halves of a real cut: the shadowed wall and the
 * lit one.
 */
function EndTurnGlyph(): JSX.Element {
  // Frame, then the two bulbs of sand, as closed paths.
  const frame = "M3 2 h12 v3 l-4.5 5 l4.5 5 v3 h-12 v-3 l4.5 -5 l-4.5 -5 z";
  const sand = "M6 4.6 h6 l-3 3.3 z M9 12.4 l3 3.6 h-6 z";
  return (
    <svg
      className="hm-adv-endturn__glyph"
      width="18"
      height="20"
      viewBox="0 0 18 20"
      aria-hidden="true"
      focusable="false"
    >
      <g strokeLinejoin="round">
        <g transform="translate(0,1.2)">
          <path d={frame} fill="rgba(255,242,206,0.34)" stroke="rgba(255,242,206,0.34)" strokeWidth="1.2" />
        </g>
        <path d={frame} fill="rgba(20,12,2,0.86)" stroke="rgba(20,12,2,0.86)" strokeWidth="1.2" />
        <path d={sand} fill="rgba(240,214,152,0.85)" />
      </g>
    </svg>
  );
}
